import {
  Asset,
  AssetHistoryResponse,
  AssetLimitStatusResponse,
  AssetMeterMeasurementsHistoryResponse,
  AssetMeterMeasurementsResponse,
  AssetMetersResponse,
  AssetPermitsResponse,
  AssetRequest,
  AssetResponse,
  AssetType,
  AssetTypeDetail,
  AssetTypeResponse,
  AssetTypesWithAssetsResponse,
  AssetWithPermitsResponse,
  ExportAssetsResponse,
  ExportAssetsTemplateResponse,
  GetAssetTypesParams,
  ImportAssetsResponse,
} from '../types/asset';
import { client } from './index';

async function getAssets(search?: string, page?: number, assetTypeId?: number) {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (page) params.append('page', page.toString());
  if (assetTypeId) params.append('asset_type_id', assetTypeId.toString());
  params.append('page_size', '50');

  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = `/company_assets${queryString}`;

  const response = await client.get<AssetResponse>(url);
  const { data } = response;
  return data;
}

async function getAssetMeterMeasurementsHistory(assetId: number, meterId: string, page: number) {
  const response = await client.get<AssetMeterMeasurementsHistoryResponse>(
    `/company_assets/${assetId}/meters/${meterId}/measurements/history?page=${page}`,
  );
  const { data } = response;
  return data;
}

async function getAsset(assetId: number) {
  const response = await client.get<AssetWithPermitsResponse>(`/company_assets/${assetId}`);
  const { data } = response;
  return data;
}

async function getAssetMeters(assetId: number) {
  const response = await client.get<AssetMetersResponse>(`/company_assets/${assetId}/meters`);
  const { data } = response;
  return data;
}

async function getAssetMeterMeasurements(assetId: number, meterId: string, page: number) {
  const response = await client.get<AssetMeterMeasurementsResponse>(
    `/company_assets/${assetId}/meters/${meterId}/measurements?page=${page}`,
  );
  const { data } = response;
  return data;
}

async function updateAssetMeterMeasurement(
  assetId: number,
  meterId: string,
  measurementId: string,
  value: number,
) {
  const response = await client.patch(
    `/company_assets/${assetId}/meters/${meterId}/measurements/${measurementId}`,
    { value },
  );
  const { data } = response;
  return data;
}

async function deleteAssetMeterMeasurement(
  assetId: number,
  meterId: string,
  measurementId: number,
) {
  const response = await client.delete<{ message: string }>(
    `/company_assets/${assetId}/meters/${meterId}/measurements/${measurementId}`,
  );
  const { data } = response;
  return data;
}

async function createAsset(assetData: AssetRequest) {
  const response = await client.post<Asset>('/company_assets', {
    asset: assetData,
  });
  const { data } = response;
  return data;
}

async function updateAsset(assetId: number, assetData: AssetRequest) {
  const response = await client.patch<Asset>(`/company_assets/${assetId}`, {
    asset: assetData,
  });
  const { data } = response;
  return data;
}

async function deleteAsset(assetId: number) {
  const response = await client.delete<{ message: string }>(`/company_assets/${assetId}`);
  const { data } = response;
  return data;
}

async function bulkDeleteAssets(assetIds: number[]) {
  const response = await client.post<{ message: string }>(`/company_assets/bulk_delete`, {
    asset_ids: assetIds,
  });
  const { data } = response;
  return data;
}

async function bulkUpdateAssetType(assetIds: number[], assetTypeId: number) {
  const response = await client.post<{ message: string }>(
    `/company_assets/bulk_update_asset_type`,
    {
      asset_ids: assetIds,
      asset_type_id: assetTypeId,
    },
  );
  const { data } = response;
  return data;
}

async function getAssetLimitStatus() {
  const response = await client.get<AssetLimitStatusResponse>('/company_assets/limit_status');
  const { data } = response;
  return data;
}

async function getAssetPermits(assetId: number) {
  const response = await client.get<AssetPermitsResponse>(`/asset_permits?asset_id=${assetId}`);
  const { data } = response;
  return data;
}

async function assignUsersToAsset(assetId: number, userIds: number[], groupIds: number[]) {
  const response = await client.patch(`/asset_permits`, {
    asset_id: assetId,
    user_ids: userIds,
    group_ids: groupIds,
  });
  const { data } = response;
  return data;
}

async function getAssetsHistory(search: string, page: number, assetTypeId?: number) {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (page) params.append('page', page.toString());
  if (assetTypeId) params.append('asset_type_id', assetTypeId.toString());
  params.append('page_size', '50');

  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = `/company_assets/history${queryString}`;

  const response = await client.get<AssetHistoryResponse>(url);
  const { data } = response;
  return data;
}

async function importAssets(file: File) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await client.post<ImportAssetsResponse>('/company_assets/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  const { data } = response;
  return data;
}

async function exportAssets(asset_type_ids?: number[]) {
  const params = asset_type_ids?.length ? { asset_type_ids } : undefined;
  const response = await client.get<ExportAssetsResponse>('/company_assets/export', { params });
  const { data } = response;
  return data;
}

async function exportAssetsTemplate() {
  const response = await client.get<ExportAssetsTemplateResponse>(
    '/company_assets/export_template',
  );
  const { data } = response;
  return data;
}

async function getAssetTypes(params: GetAssetTypesParams) {
  const response = await client.get<AssetTypeResponse>('/asset_types', { params });
  const { data } = response;
  return data;
}

async function getAllAssetTypes(params: GetAssetTypesParams) {
  const response = await client.get<{ asset_types: AssetType[] }>('/all_asset_types', { params });
  const { data } = response;
  return data;
}

async function getAssetTypesWithAssets(params: GetAssetTypesParams) {
  const response = await client.get<AssetTypesWithAssetsResponse>('/asset_types/with_assets', {
    params,
  });
  const { data } = response;
  return data;
}

async function initializeAssetTypes() {
  await client.post<void>('/asset_types/initialize_templates');
}

async function getAssetType(assetTypeId: number) {
  const response = await client.get<AssetType>(`/asset_types/${assetTypeId}`);
  const { data } = response;
  return data;
}

async function createAssetType(assetType: AssetTypeDetail) {
  const response = await client.post<AssetType>('/asset_types', {
    asset_type: assetType,
  });
  const { data } = response;
  return data;
}

async function deleteAssetType(assetTypeId: number) {
  await client.delete<void>(`/asset_types/${assetTypeId}`);
}

async function updateAssetType(assetTypeId: number, assetType: AssetTypeDetail) {
  const response = await client.patch<AssetType>(`/asset_types/${assetTypeId}`, {
    asset_type: assetType,
  });
  const { data } = response;
  return data;
}

export const assetsApi = {
  getAssetTypesWithAssets,
  getAssets,
  getAsset,
  createAsset,
  updateAsset,
  deleteAsset,
  getAssetPermits,
  assignUsersToAsset,
  getAssetsHistory,
  importAssets,
  exportAssets,
  exportAssetsTemplate,
  getAssetMeters,
  getAssetMeterMeasurements,
  getAssetMeterMeasurementsHistory,
  updateAssetMeterMeasurement,
  deleteAssetMeterMeasurement,
  getAssetTypes,
  getAllAssetTypes,
  getAssetType,
  initializeAssetTypes,
  deleteAssetType,
  updateAssetType,
  createAssetType,
  bulkDeleteAssets,
  bulkUpdateAssetType,
  getAssetLimitStatus,
};
